import { Check, X, AlertTriangle, ShieldAlert, Sword, Globe, Users, FileText, Languages, Lock } from "lucide-react";

const ROWS = [
  {
    icon: AlertTriangle,
    feature: "Sycophancy-resistentie",
    solo: "Tendeert tot bevestiging — modellen zijn getraind om behulpzaam en aangenaam te klinken.",
    de: "Elke specialist heeft een vast professional_anchor dat tegenspraak afdwingt; meetbaar via onze interne Sycophancy Index.",
    deWins: true,
  },
  {
    icon: ShieldAlert,
    feature: "Hallucinatie-controle",
    solo: "Geen ingebouwde tweede beoordeling — de modeloutput is direct het eindresultaat.",
    de: "Premium Arbiter scant het debat onafhankelijk op hallucinaties, drogredenen en weggemoffelde meningsverschillen.",
    deWins: true,
  },
  {
    icon: Users,
    feature: "Vakspecifiek perspectief",
    solo: "Eén generalistisch model met algemene kennis, soms aanvulbaar via custom GPTs.",
    de: "52 rollen waarvan 14 anchored specialisten — een Ecoloog argumenteert echt vanuit Natura2000, een Litigator echt vanuit bewijslast.",
    deWins: true,
  },
  {
    icon: Sword,
    feature: "Cross-examination",
    solo: "Niet aanwezig — geen verplichte clash tussen perspectieven.",
    de: "Verplicht kruisverhoor: elke specialist stelt één scherpe vraag aan elke andere, blootlegt waar disciplines botsen.",
    deWins: true,
  },
  {
    icon: Globe,
    feature: "Actuele internetdata",
    solo: "Beperkt tot training cut-off; live-zoeken is beperkt of betaald via aparte tools.",
    de: "Live-verificatie via Perplexity Sonar checkt de strongest claims tegen recente bronnen.",
    deWins: true,
  },
  {
    icon: FileText,
    feature: "Audit trail / export",
    solo: "Conversatie is exporteerbaar maar één-stem; geen rollenscheiding.",
    de: "Per debat: PDF (Quick / Full / Executive Summary), Arbiter-rapport, kruisverhoor-log.",
    deWins: true,
  },
  {
    icon: Languages,
    feature: "Communicatieniveau",
    solo: "Eén stijl tegelijk; herformuleren kost extra prompt-rondes.",
    de: "Toggle: Simpel (B1) — Standaard — Diep. Eén klik op de synthese.",
    deWins: true,
  },
  {
    icon: Lock,
    feature: "Compliance / hosting",
    solo: "Vaak Amerikaans gehost; voorwaarden over training-data en bewaartermijn variëren.",
    de: "EU-conform, geen tracking-cookies, geen verkoop van debat-inhoud aan derden.",
    deWins: true,
  },
];

const HONEST_FALLBACK = {
  title: "Waar solo-LLM's wél beter zijn (eerlijk)",
  points: [
    "Snelheid bij triviale taken: 'wat is de hoofdstad van X?' — daar is een debat overkill.",
    "Brainstorm zonder kritiek: als je creatief wilt divergeren, kan een solo-model losser zijn.",
    "Prijs per query: één API-call is goedkoper dan een 4-stemmig debat met arbiter.",
  ],
};

export const DebateEngineVsChatGPT = () => {
  return (
    <section className="my-12 border-t-2 border-gray-200 pt-10" data-testid="vs-chatgpt-section">
      <div className="text-center mb-8">
        <p className="text-[11px] font-mono uppercase tracking-widest text-gray-500 mb-2">Eerlijke vergelijking</p>
        <h2 className="text-2xl sm:text-3xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>
          Debate Engine vs. solo-LLM
        </h2>
        <p className="text-sm text-gray-600 mt-2 max-w-xl mx-auto">
          Wat ChatGPT, Claude of Gemini solo wél en niet doen — en waar onze specifieke agents het verschil maken.
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border-2 border-gray-200 overflow-hidden" data-testid="vs-table-desktop">
        <div className="grid grid-cols-12 bg-gray-900 text-white text-xs font-bold uppercase tracking-wider">
          <div className="col-span-4 px-4 py-3">Kenmerk</div>
          <div className="col-span-4 px-4 py-3 border-l border-gray-700">Solo-LLM (ChatGPT / Claude / Gemini)</div>
          <div className="col-span-4 px-4 py-3 border-l border-gray-700 bg-[#002FA7]">Debate Engine</div>
        </div>
        {ROWS.map((r, i) => (
          <div key={r.feature} className={`grid grid-cols-12 text-xs ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`} data-testid={`vs-row-${i}`}>
            <div className="col-span-4 px-4 py-4 flex items-start gap-2 font-bold">
              <r.icon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <span>{r.feature}</span>
            </div>
            <div className="col-span-4 px-4 py-4 border-l border-gray-200 flex items-start gap-2 text-gray-700">
              <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{r.solo}</span>
            </div>
            <div className="col-span-4 px-4 py-4 border-l border-gray-200 flex items-start gap-2 text-gray-900 bg-[#002FA7]/5">
              <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{r.de}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-4" data-testid="vs-table-mobile">
        {ROWS.map((r, i) => (
          <div key={r.feature} className="border-2 border-gray-200 bg-white" data-testid={`vs-card-${i}`}>
            <div className="bg-gray-900 text-white px-3 py-2 flex items-center gap-2">
              <r.icon className="w-4 h-4" />
              <span className="font-bold text-sm">{r.feature}</span>
            </div>
            <div className="px-3 py-3 border-b border-gray-100">
              <p className="text-[10px] font-mono uppercase text-gray-500 mb-1">Solo-LLM</p>
              <p className="text-xs text-gray-700 flex items-start gap-2">
                <X className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
                {r.solo}
              </p>
            </div>
            <div className="px-3 py-3 bg-[#002FA7]/5">
              <p className="text-[10px] font-mono uppercase text-[#002FA7] mb-1">Debate Engine</p>
              <p className="text-xs text-gray-900 flex items-start gap-2">
                <Check className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                {r.de}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Honest fallback */}
      <div className="mt-6 bg-amber-50 border-l-4 border-amber-400 px-4 py-3" data-testid="honest-fallback">
        <p className="text-xs font-bold text-amber-900 mb-2">{HONEST_FALLBACK.title}</p>
        <ul className="space-y-1">
          {HONEST_FALLBACK.points.map((p) => (
            <li key={p} className="text-[11px] text-amber-800 flex items-start gap-2">
              <span className="text-amber-600 mt-0.5">·</span>{p}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-[10px] font-mono text-gray-400 text-center mt-4">
        Vergelijking gebaseerd op feature-pariteit per feb 2026. Solo-LLM's evolueren — wij ook.
      </p>
    </section>
  );
};
